import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { clearStoredToken, fetchCurrentUser, getStoredToken, login as apiLogin, storeToken } from "../api/auth";
import { clearStoredChatSessions } from "../hooks/useChatSessions";
import type { AuthUser } from "../types";

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Restore a session from a previously stored token on first load.
  useEffect(() => {
    const token = getStoredToken();

    if (!token) {
      setIsLoading(false);
      return;
    }

    fetchCurrentUser(token).then((restoredUser) => {
      if (restoredUser) {
        setUser(restoredUser);
      } else {
        // Token expired/invalid: this is an implicit logout, so wipe the
        // previous session's chat history too, not just the dead token.
        clearStoredToken();
        clearStoredChatSessions();
      }
      setIsLoading(false);
    });
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const { token, user: loggedInUser } = await apiLogin(username, password);
    storeToken(token);
    // Chat history is per-login: a fresh sign-in should never resume
    // whatever session was left over from whoever was last logged in.
    clearStoredChatSessions();
    setUser(loggedInUser);
  }, []);

  const logout = useCallback(() => {
    clearStoredToken();
    clearStoredChatSessions();
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, isLoading, login, logout }), [user, isLoading, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
